import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { localizeAsset } from "@/lib/assets";
import { Loader } from "@/components/AppShell";
import { ArrowLeft, Flame, ExternalLink, Eye } from "lucide-react";

export default function ChiSono() {
  const [bio, setBio] = useState(null);
  const [params] = useSearchParams();
  const preview = params.get("preview") === "1";
  const navigate = useNavigate();

  useEffect(() => {
    const req = preview
      ? api.get("/admin/bio").then((r) => ({ page_enabled: true, ...(r.data?.draft || {}) }))
      : api.get("/bio").then((r) => r.data);
    req.then(setBio).catch(() => setBio({ page_enabled: false }));
  }, [preview]);

  if (!bio) return <Loader />;

  if (bio.page_enabled === false) {
    return (
      <div className="anima-backdrop min-h-screen flex items-center justify-center px-7 text-center">
        <div>
          <p className="font-serif text-2xl text-anima-ink mb-4">Questa pagina non è al momento disponibile.</p>
          <button onClick={() => navigate(-1)} className="rounded-full bg-anima-sagedeep text-anima-ivory px-6 py-3" data-testid="chisono-unavailable-back">Indietro</button>
        </div>
      </div>
    );
  }

  const links = (bio.links || []).filter((l) => l.enabled && (l.url || "").trim());

  return (
    <div className="anima-backdrop min-h-screen w-full flex justify-center">
      <div className="w-full max-w-md px-7 py-10">
        <button onClick={() => navigate(-1)} className="text-anima-muted mb-7 flex items-center gap-1 text-base" data-testid="chisono-back">
          <ArrowLeft size={16} /> Indietro
        </button>

        {preview && (
          <div className="mb-6 flex items-center gap-2 rounded-2xl bg-anima-gold/15 border border-anima-gold/30 px-4 py-3 text-[13px] text-anima-ink" data-testid="chisono-preview-banner">
            <Eye size={15} className="text-anima-golddeep" /> Anteprima della bozza (non ancora pubblicata)
          </div>
        )}

        <div className="flex items-center gap-2 mb-2 text-anima-golddeep">
          <Flame size={16} />
          <span className="text-[11px] uppercase tracking-widest">Chi ha ideato Ascolta Anima</span>
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          className="font-serif text-[44px] leading-[1.02] text-anima-ink" data-testid="chisono-title"
        >
          Anna Checchi
        </motion.h1>
        <p className="font-serif text-xl text-anima-muted italic mt-1">{bio.subtitle}</p>
        <p className="text-[13px] text-anima-golddeep/90 leading-relaxed mt-3">{bio.qualifications}</p>

        {bio.portrait_url ? (
          <motion.figure
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }}
            className="my-8"
          >
            <img
              src={localizeAsset(bio.portrait_url)}
              alt={bio.portrait_alt || "Ritratto di Anna Checchi, ideatrice di Ascolta Anima."}
              className="w-full h-auto block rounded-2xl border border-anima-ink/10 shadow-[0_16px_48px_rgba(74,69,67,0.14)]"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              style={{ userSelect: "none" }}
              data-testid="chisono-portrait"
            />
          </motion.figure>
        ) : (
          <div className="my-8" />
        )}

        {bio.intro && (
          <p className="font-serif text-[19px] text-anima-ink/90 leading-relaxed mb-8" data-testid="chisono-intro">{bio.intro}</p>
        )}

        <div className="space-y-8">
          {(bio.sections || []).map((s, i) => (
            <motion.section
              key={`${i}-${s.title || "sec"}`}
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6 }}
              data-testid={`chisono-section-${i}`}
            >
              {s.title && <h2 className="font-serif text-2xl text-anima-ink leading-snug mb-3">{s.title}</h2>}
              <p className="text-base text-anima-ink/85 leading-relaxed whitespace-pre-line">{s.body}</p>
            </motion.section>
          ))}
        </div>

        {bio.quote_text && (
          <figure className="my-10 rounded-3xl bg-anima-sage/15 border-l-2 border-anima-gold/60 pl-6 pr-5 py-6" data-testid="chisono-quote">
            <blockquote className="font-serif text-[21px] text-anima-ink italic leading-relaxed">“{bio.quote_text}”</blockquote>
            <figcaption className="text-sm text-anima-muted mt-3">— {bio.quote_author || "Anna Checchi"}</figcaption>
          </figure>
        )}

        <section className="mt-10 rounded-3xl bg-anima-purple/25 border border-anima-ink/5 p-6" data-testid="chisono-ianas-link">
          <div className="flex items-center gap-2 mb-2">
            <Flame size={16} className="text-anima-golddeep" />
            <h2 className="font-serif text-xl text-anima-ink leading-tight">Arte come connessione con lo Spirito</h2>
          </div>
          <p className="text-[15px] text-anima-ink/85 leading-relaxed">
            Tra le opere che esprimono questa visione, Ianas occupa un posto particolare. Le tre figure danzano intorno al Fuoco dello Spirito, immagine di una luce interiore capace di accompagnare trasformazione, chiarezza e rinascita.
          </p>
          <button
            onClick={() => navigate("/ianas")}
            className="mt-4 text-[15px] text-anima-sagedeep font-medium"
            data-testid="chisono-ianas-btn"
          >
            Scopri Ianas →
          </button>
        </section>

        {links.length > 0 && (
          <section className="mt-10" data-testid="chisono-links">
            <div className="h-px w-10 bg-anima-gold/60 mb-5" />
            <div className="space-y-3">
              {links.map((l) => (
                <a
                  key={l.key}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between rounded-2xl bg-white/55 border border-anima-ink/8 px-5 py-4 text-anima-ink active:scale-[0.98] transition-transform duration-200"
                  data-testid={`chisono-link-${l.key}`}
                >
                  <span className="text-[15px]">{l.label}</span>
                  <ExternalLink size={16} className="text-anima-muted" />
                </a>
              ))}
            </div>
          </section>
        )}

        <div className="h-8" />
      </div>
    </div>
  );
}
